import * as React from "react";
import type { User } from "../services/auth";
import { checkAuthUser, loginUser, logoutUser } from "../services/auth";
import { clearTokens } from "../services/tokenStorage";

interface AuthContextType {
	user: User | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	checkAuth: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = React.useState<User | null>(null);
	const [isLoading, setIsLoading] = React.useState(true);

	const isAuthenticated = !!user;

	const login = React.useCallback(async (email: string, password: string) => {
		setIsLoading(true);
		try {
			const loggedInUser = await loginUser(email, password);
			setUser(loggedInUser);
		} catch (error) {
			console.error("Login error:", error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, []);

	const logout = React.useCallback(async () => {
		try {
			await logoutUser();
		} catch (error) {
			console.error("Logout error:", error);
		} finally {
			clearTokens();
			setUser(null);
		}
	}, []);

	const checkAuth = React.useCallback(async () => {
		setIsLoading(true);
		try {
			console.log("[AuthContext] Checking auth...");
			const authUser = await checkAuthUser();
			console.log("[AuthContext] User found:", authUser);
			setUser(authUser);
		} catch (error: any) {
			console.log("[AuthContext] Auth check failed:", error);

			// Only clear tokens if it's a real auth error (401/403), not a network error
			// The apiFetch should have already tried to refresh the token
			if (error?.status === 401 || error?.status === 403) {
				console.log("[AuthContext] Authentication failed, clearing tokens");
				clearTokens();
				setUser(null);
			} else if (error?.isNetworkError) {
				// Network error - don't clear tokens, just log it
				console.log("[AuthContext] Network error, keeping tokens");
				// Keep user state as is (might be null if first load)
			} else {
				// Other errors - might be temporary, don't clear tokens
				console.log("[AuthContext] Other error, keeping tokens:", error);
			}
		} finally {
			setIsLoading(false);
		}
	}, []);

	React.useEffect(() => {
		console.log("[AuthContext] Mounting, checking auth...");
		checkAuth();
		// Only run on mount
	}, [checkAuth]);

	const value: AuthContextType = {
		user,
		isLoading,
		isAuthenticated,
		login,
		logout,
		checkAuth,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = React.useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
